import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/util/cn';

// Page-width wrapper. Replaces the `container-xl content-container` div
// pattern that wraps every page today. Picks up the responsive horizontal
// padding from Tailwind's default scale.

const containerVariants = cva(
  '',
  {
    variants: {
      width: {
        narrow: 'mx-auto w-full px-4 sm:px-6 lg:px-8 max-w-3xl', // 768px — long-form copy
        standard: 'mx-auto w-full px-4 sm:px-6 lg:px-8 max-w-6xl', // 1152px — today's xl breakpoint
        wide: 'mx-auto w-full px-4 sm:px-6 lg:px-8 max-w-7xl', // 1280px — data-rich pages
        full: 'mx-auto w-full px-4 sm:px-6 lg:px-8 max-w-full', // edge-to-edge
        // 'xl-only' matches the legacy containerXlClass: tighter horizontal
        // padding, max-width applies only at xl+. Used by page chrome
        // (HeaderNav, Footer, error pages) where the nav bar wants its own
        // !px override at smaller breakpoints.
        // Mobile keeps the tight px-3 to leave room for Layout's
        // own max-md:px-4. From md onward Layout drops its padding,
        // so the container has to carry it — bump to a real
        // progression (24px / 32px) until max-w-7xl kicks in at xl.
        'xl-only': 'mx-auto w-full px-3 md:px-6 lg:px-8 xl:max-w-7xl',
      },
    },
    defaultVariants: { width: 'standard' },
  },
);

type ContainerTag = 'div' | 'section' | 'header' | 'main' | 'article' | 'footer' | 'nav';

export interface ContainerProps
  extends React.HTMLAttributes<HTMLElement>,
  VariantProps<typeof containerVariants> {
  as?: ContainerTag;
}

const Container = React.forwardRef<HTMLElement, ContainerProps>(
  ({
    as: Tag = 'div', width, className, ...props
  }, ref) => (
    <Tag
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      ref={ref as any}
      className={cn(containerVariants({ width }), className)}
      {...props}
    />
  ),
);
Container.displayName = 'Container';

// Drop-in replacement for the legacy Bootstrap `.container` stepped-ladder
// chrome — kept as a string export because some surfaces (Layout/Footer)
// still rely on this for their inner padding ladder, and it doesn't fit
// cleanly into the cva variants.
export const containerClass = [
  'mx-auto w-full px-3',
  'sm:max-w-[540px] md:max-w-[720px] lg:max-w-[960px]',
  'xl:max-w-7xl 2xl:max-w-[1320px]',
].join(' ');

export { Container, containerVariants };
// containerXlClass retired — use <Container width="xl-only"> instead.
